/**
 * Client-side error logging utility
 * Sends errors to the server for logging with batching and deduplication
 */

interface ErrorLogData {
  message: string;
  stack?: string;
  url?: string;
  line?: number;
  column?: number;
  error?: string;
  timestamp: string;
  userAgent?: string;
  route?: string;
  statusCode?: number;
  responseBody?: unknown;
}

interface BatchedError extends ErrorLogData {
  count: number;
  firstSeen: string;
  lastSeen: string;
}

// Error buffer for batching - keyed by deduplication key
const errorBuffer = new Map<string, BatchedError>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH_WINDOW_MS = 5000; // 5 seconds

/**
 * Generate a deduplication key for an error
 * Uses error message and route to group similar errors
 */
function getDedupeKey(errorData: ErrorLogData): string {
  // Normalize the message by removing dynamic parts like line numbers, timestamps, UUIDs
  const normalizedMessage = errorData.message
    ?.replace(/:\d+:\d+/g, ':X:X') // Replace line:column numbers
    ?.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, 'UUID') // Replace UUIDs
    ?.replace(/\d{13,}/g, 'TIMESTAMP') // Replace timestamps
    ?.substring(0, 200); // Limit length for key

  const route = typeof window !== 'undefined' ? window.location.pathname : '';
  return `${normalizedMessage}::${route}`;
}

function shouldFilterError(errorData: ErrorLogData): boolean {
  const { message, stack } = errorData;

  // Filter hydration errors
  if (
    message?.includes('Hydration failed') ||
    message?.includes('Text content does not match') ||
    message?.includes('useLayoutEffect does nothing on the server')
  ) {
    return true;
  }

  // Filter React development warnings
  if (
    message?.includes('Warning: Prop `className` did not match') ||
    message?.includes('Warning: Prop `style` did not match') ||
    message?.includes('Warning: Prop `children` did not match') ||
    message?.includes('React does not recognize')
  ) {
    return true;
  }

  return false;
}

/**
 * Flush all batched errors to the server
 * Initiates all fetches in parallel to ensure they start before page unload
 */
async function flushErrorBuffer(): Promise<void> {
  if (errorBuffer.size === 0) {
    return;
  }

  // Take a snapshot of the buffer and clear it
  const errorsToSend = Array.from(errorBuffer.values());
  errorBuffer.clear();
  flushTimer = null;

  // Build all fetch promises synchronously so requests are initiated immediately
  const promises = errorsToSend.map(errorData => {
    const payload = JSON.stringify({
      ...errorData,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      // Include batching metadata
      batchedCount: errorData.count,
      batchedFirstSeen: errorData.firstSeen,
      batchedLastSeen: errorData.lastSeen,
    });

    // Try sendBeacon first as it's more reliable during page unload
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      const sent = navigator.sendBeacon('/api/log-client-error', blob);
      if (sent) {
        return Promise.resolve();
      }
      // Fall through to fetch if sendBeacon fails (e.g., payload too large)
    }

    // Fallback to fetch with keepalive
    return fetch('/api/log-client-error', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // Silently fail - we don't want error logging to cause more errors
      console.error('Failed to log client error to server');
    });
  });

  // Wait for all requests to settle (don't fail fast on individual errors)
  await Promise.allSettled(promises);
}

/**
 * Schedule a flush if one isn't already pending
 */
function scheduleFlush(): void {
  if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      flushErrorBuffer();
    }, BATCH_WINDOW_MS);
  }
}

/**
 * Add an error to the buffer for batched sending
 */
function bufferError(errorData: ErrorLogData): void {
  const key = getDedupeKey(errorData);
  const now = new Date().toISOString();

  const existing = errorBuffer.get(key);
  if (existing) {
    // Update existing entry - increment count and update lastSeen
    existing.count += 1;
    existing.lastSeen = now;
    // Keep the most recent stack trace
    if (errorData.stack) {
      existing.stack = errorData.stack;
    }
  } else {
    // Add new entry
    errorBuffer.set(key, {
      ...errorData,
      count: 1,
      firstSeen: now,
      lastSeen: now,
    });
  }

  scheduleFlush();
}

export async function logClientError(errorData: ErrorLogData): Promise<void> {
  // Skip logging if error should be filtered
  if (shouldFilterError(errorData)) {
    return;
  }

  // Add to buffer instead of sending immediately
  bufferError(errorData);
}

export function logError(error: Error | string, context?: Record<string, unknown>): void {
  const errorData: ErrorLogData = {
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    timestamp: new Date().toISOString(),
    ...context,
  };

  logClientError(errorData);
}

export function logUnhandledError(
  message: string,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): void {
  const errorData: ErrorLogData = {
    message,
    stack: error?.stack,
    url: source,
    line: lineno,
    column: colno,
    error: error?.toString(),
    timestamp: new Date().toISOString(),
  };

  logClientError(errorData);
}

export function logUnhandledRejection(reason: unknown, promise: Promise<unknown>): void {
  const errorData: ErrorLogData = {
    message: reason instanceof Error ? reason.message : String(reason),
    stack: reason instanceof Error ? reason.stack : undefined,
    error: String(reason),
    timestamp: new Date().toISOString(),
  };

  logClientError(errorData);
}

export function logFetchError(url: string, status: number, statusText: string, responseBody?: unknown): void {
  const errorData: ErrorLogData = {
    message: `Fetch error: ${status} ${statusText}`,
    url,
    statusCode: status,
    responseBody,
    timestamp: new Date().toISOString(),
  };

  logClientError(errorData);
}

/**
 * Force flush any pending errors immediately
 * Useful for page unload or when you need to ensure errors are sent
 */
export function flushErrors(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flushErrorBuffer();
}

// Flush errors before page unload to avoid losing them
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    flushErrors();
  });

  // Also use visibilitychange for mobile browsers that may not fire beforeunload
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushErrors();
    }
  });
}
